/**
 * Shared service request seed data and helpers
 */

import type { ServiceRequest as FlowServiceRequest } from "@/contexts/ServiceFlowContext";

export type ServiceRequestSeed = FlowServiceRequest & { notes?: string };

export const SERVICE_REQUESTS_STORAGE_KEY = "biosam-service-requests";
export const SERVICE_REQUESTS_EVENT = "biosam:service-requests-update";

export const defaultServiceRequests: ServiceRequestSeed[] = [
    {
        id: "REQ-2026-001",
        clientId: "hotel-meridian",
        clientName: "Hotel Meridian",
        clientEmail: "cliente@hotelmeridian.com",
        clientPhone: "934567890",
        type: "Desratización",
        description:
            "Se han detectado signos de roedores en el sótano del hotel. Necesitamos inspección y tratamiento urgente.",
        urgency: "high",
        preferredDate: "2026-01-28",
        status: "pending",
        createdAt: new Date("2026-01-27T09:30:00"),
    },
    {
        id: "REQ-2026-002",
        clientId: "rest-buen-sabor",
        clientName: "Restaurante El Buen Sabor",
        clientEmail: "info@elbuensabor.com",
        clientPhone: "912345678",
        type: "Control de Cucarachas",
        description:
            "Mantenimiento preventivo mensual programado. Revisar cocina y almacén.",
        urgency: "medium",
        preferredDate: "2026-02-01",
        status: "reviewing",
        createdAt: new Date("2026-01-26T14:20:00"),
    },
    {
        id: "REQ-2026-003",
        clientId: "oficinas-techcorp",
        clientName: "Oficinas TechCorp",
        clientEmail: "facilities@techcorp.com",
        clientPhone: "923456789",
        type: "Control de Hormigas",
        description:
            "Aparición de hormigas en la zona de oficinas del tercer piso. Preferiblemente tratamiento fuera de horario laboral.",
        urgency: "low",
        preferredDate: "2026-02-05",
        status: "approved",
        createdAt: new Date("2026-01-25T11:45:00"),
    },
    {
        id: "REQ-2026-004",
        clientId: "colegio-san-juan",
        clientName: "Colegio San Juan",
        clientEmail: "admin@colegiosanjuan.edu",
        clientPhone: "934123456",
        type: "Inspección General",
        description:
            "Inspección trimestral de instalaciones escolares según normativa sanitaria.",
        urgency: "medium",
        preferredDate: "2026-01-30",
        status: "scheduled",
        createdAt: new Date("2026-01-24T16:10:00"),
    },
];

interface SerializedRequest
    extends Omit<ServiceRequestSeed, "createdAt"> {
    createdAt: string;
}

export const serializeServiceRequests = (
    requests: ServiceRequestSeed[],
): string =>
    JSON.stringify(
        requests.map((request) => ({
            ...request,
            createdAt: request.createdAt.toISOString(),
        })),
    );

const cloneDefaults = (): ServiceRequestSeed[] =>
    defaultServiceRequests.map((request) => ({
        ...request,
        createdAt: new Date(request.createdAt),
    }));

export const deserializeServiceRequests = (
    raw: string | null,
): ServiceRequestSeed[] => {
    if (!raw) {
        return cloneDefaults();
    }

    try {
        const parsed = JSON.parse(raw) as SerializedRequest[];
        return parsed.map((request) => ({
            ...request,
            createdAt: new Date(request.createdAt),
        }));
    } catch (error) {
        console.warn("Error parsing stored service requests", error);
        return cloneDefaults();
    }
};

export const hasHighUrgencyRequest = (
    requests: ServiceRequestSeed[],
): boolean => requests.some((request) => request.urgency === "high");

export const readStoredUrgencyFlag = (): boolean => {
    if (typeof window === "undefined") return false;

    const raw = window.localStorage.getItem(SERVICE_REQUESTS_STORAGE_KEY);
    if (!raw) return false;

    try {
        const parsed = JSON.parse(raw) as Array<{ urgency?: string }>;
        if (!Array.isArray(parsed)) return false;
        return parsed.some((r) => r.urgency === "high");
    } catch {
        return false;
    }
};
